import { useEffect, useState } from "react";
import { Menu, X, Languages } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

export default function Header() {
    const { t, lang, setLang } = useT();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { href: "#story", label: t("nav.mission"), key: "mission" },
        { href: "#how", label: t("nav.how"), key: "how" },
        { href: "#stats", label: t("nav.impact"), key: "impact" },
        { href: "#founder", label: t("nav.founder"), key: "founder" },
        { href: "#waitlist", label: t("nav.join"), key: "join" },
    ];

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
                scrolled
                    ? "bg-background/85 backdrop-blur-md border-b border-border/60 text-primary"
                    : "bg-transparent text-primary-foreground"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
                <a
                    href="#top"
                    aria-label="CHIGLE — home"
                    data-testid="site-logo"
                    className={`font-display text-2xl md:text-3xl tracking-tight uppercase ${scrolled ? "text-primary" : "text-primary-foreground"}`}
                >
                    CHIGLE<span className="text-accent">.</span>
                </a>

                <nav aria-label="Primary" className="hidden md:flex items-center gap-9">
                    {links.map((l) => (
                        <a
                            key={l.key}
                            href={l.href}
                            data-testid={`nav-${l.key}`}
                            className={`text-sm tracking-wide transition-colors ${scrolled ? "text-foreground/80 hover:text-primary" : "text-primary-foreground/90 hover:text-accent"}`}
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-4">
                    <LanguageToggle scrolled={scrolled} lang={lang} setLang={setLang} t={t} />
                    <a
                        href="#waitlist"
                        data-testid="header-cta"
                        className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm tracking-wide rounded-full transition-all ${scrolled ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-accent text-primary-foreground hover:bg-accent/90"}`}
                    >
                        {t("header.cta")}
                    </a>
                </div>

                <button
                    type="button"
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    aria-controls="mobile-menu"
                    data-testid="mobile-menu-toggle"
                    className={`md:hidden ${scrolled ? "text-primary" : "text-primary-foreground"}`}
                    onClick={() => setOpen((v) => !v)}
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    id="mobile-menu"
                    data-testid="mobile-menu"
                    className="md:hidden bg-background border-t border-border/60"
                >
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {links.map((l) => (
                            <a
                                key={l.key}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${l.key}`}
                                className="font-display text-2xl text-primary"
                            >
                                {l.label}
                            </a>
                        ))}
                        <div className="pt-4 mt-2 border-t border-primary/10">
                            <LanguageToggle scrolled={true} lang={lang} setLang={setLang} t={t} mobile />
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}

function LanguageToggle({ scrolled, lang, setLang, t, mobile = false }) {
    const isHi = lang === "hi";
    return (
        <div
            role="group"
            aria-label={t("lang.switch.label")}
            data-testid="lang-toggle"
            className={`inline-flex items-center gap-1 rounded-full border ${scrolled ? "border-primary/25" : "border-primary-foreground/30"} ${mobile ? "self-start" : ""}`}
        >
            <Languages
                size={14}
                className={`ml-2.5 ${scrolled ? "text-primary/70" : "text-primary-foreground/80"}`}
                aria-hidden="true"
            />
            <button
                type="button"
                onClick={() => setLang("en")}
                data-testid="lang-en"
                aria-pressed={!isHi}
                className={`px-2.5 py-1 text-xs tracking-wide rounded-full transition-colors ${!isHi ? (scrolled ? "bg-primary text-primary-foreground" : "bg-accent text-primary-foreground") : (scrolled ? "text-primary/70 hover:text-primary" : "text-primary-foreground/80 hover:text-primary-foreground")}`}
            >
                EN
            </button>
            <button
                type="button"
                onClick={() => setLang("hi")}
                data-testid="lang-hi"
                aria-pressed={isHi}
                className={`px-2.5 py-1 mr-0.5 text-xs tracking-wide rounded-full transition-colors ${isHi ? (scrolled ? "bg-primary text-primary-foreground" : "bg-accent text-primary-foreground") : (scrolled ? "text-primary/70 hover:text-primary" : "text-primary-foreground/80 hover:text-primary-foreground")}`}
            >
                हिं
            </button>
        </div>
    );
}
